/**
 * ReportCardItem — card component for report tiles in carousel grids
 * (Standard, Explorer, Custom, Favorites, Exclusive, etc.).
 *
 * Matches the Recently Viewed card layout from ReportSummaryItem
 * (border, radius, padding, typography, action row) but is self-contained
 * with its own callbacks so it can be used from bstCard.js without a parent
 * supplying handlers.
 */
import { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import { store } from 'store/store';
import { formatReptSource } from 'utils/formatReptSource';
import { trackClick, trackingActions, trackingTypes } from 'utils/tracking';
import { useShowToolTip } from 'common/hooks/useShowToolTip';
import StyledTooltip from 'common/StyledTooltip';
import { overflowTooltipSx, overflowArrowSx, overflowPopperModifiers } from './overflowTooltipStyles';
import * as ReportTypes from 'features/frontoffice/actions/types';
import ReportOperation from '../../features/landing/newHome/ReportOperation';
import CardTitle from './cardTitle';
import * as Constant from 'common/Constant';

const ReportCardItem = ({ item, showFavorite = true, reportType, viewMode = 'grid' }) => {
  const currentTenantCd = useSelector((state) => state.user.currentTenantCd);
  const tableauServiceUrl = useSelector((state) => state.user.tableauServiceUrl);
  const navigate = useNavigate();
  const location = useLocation();

  const titleRef = useRef();
  const descRef = useRef();
  const isTitleOverflowing = useShowToolTip(titleRef);
  const isDescOverflowing = useShowToolTip(descRef);

  // Favorites are available for every report type rendered here, including Exclusive,
  // to match Custom and Ad Hoc.
  const shouldShowFavorite = showFavorite;

  const descriptionText =
    item.description && String(item.description).trim().length > 0
      ? item.description
      : 'No description available.';

  const handleReportClick = useCallback(
    (card) => {
      const target = card || item;
      if (target?.id) {
        trackClick(
          trackingTypes.REPORT,
          trackingActions.READ,
          target.id,
          (target.category ?? '') + ';' + (target.displayText ?? '')
        );
      }

      let reportOriginComponent = '';
      let reportBaseUrl = '/bi';
      if (location.pathname.includes('/reports')) {
        reportBaseUrl = '/bi/reports';
        reportOriginComponent = Constant.REPORTS;
      } else if (location.pathname.includes('/smart-cards')) {
        reportBaseUrl = '/bi/smart-cards';
      }

      const [workbook, dashboard] = (target.reptSource || '').split('/');
      navigate(`/bi/${currentTenantCd}/view/${workbook}/${dashboard}`);

      store.dispatch({
        type: ReportTypes.OPEN_REPORT_VIEWER_REQUEST,
        reportUrl: target.reportSource
          ? target.reportSource
          : formatReptSource(tableauServiceUrl, currentTenantCd, target.reptSource),
        category: target.category,
        title: target.displayText,
        reportBaseUrl,
        report: target,
        reportOriginComponent
      });
      sessionStorage.setItem('reportOrigin', JSON.stringify({ reportBaseUrl, reportOriginComponent }));
    },
    [currentTenantCd, tableauServiceUrl, navigate, location.pathname, item]
  );

  const handleShareClick = useCallback((card) => {
    alert('Share clicked');
  }, []);

  const card = item;

  if (viewMode === 'list') {
    return (
      <Box
        sx={{
          width: '100%',
          maxWidth: '1584px',
          minWidth: 0,
          border: '1px solid #71767A',
          borderRadius: '16px',
          p: '24px',
          bgcolor: '#FFF',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          overflow: 'hidden'
        }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '24px', width: '100%' }}>
          <Box sx={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <CardTitle
              card={item}
              showEye={false}
              titleRef={titleRef}
              isTitleOverflowing={isTitleOverflowing}
              fallbackTitle="Report Title"
              containerSx={{ alignItems: 'center' }}
              titleSx={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: '#000',
                fontFamily: 'Rubik',
                fontSize: '16px',
                fontWeight: 600,
                lineHeight: '150%',
                letterSpacing: '0.16px'
              }}
            />
            <StyledTooltip title={descriptionText} disableHoverListener={!isDescOverflowing} placement="bottom-end" tooltipSx={overflowTooltipSx} arrowSx={overflowArrowSx} popperModifiers={overflowPopperModifiers}>
              <Box
                ref={descRef}
                sx={{
                  color: '#121A22',
                  fontFamily: 'Noto Sans',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '150%',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                {descriptionText}
              </Box>
            </StyledTooltip>
          </Box>
          <Box sx={{ flex: '0 0 auto' }}>
            <ReportOperation
              card={card}
              handleReportClick={handleReportClick}
              handleShareClick={handleShareClick}
              shouldShowFavorite={shouldShowFavorite}
              containerPx={0}
              compact
            />
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: '#FFF',
        borderRadius: '16px',
        width: '100%',
        height: '245px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        position: 'relative',
        boxSizing: 'border-box'
      }}
    >
      <Box
        sx={{
          borderTop: '1px solid #71767A',
          borderRight: '1px solid #71767A',
          borderLeft: '1px solid #71767A',
          borderBottom: 'none',
          borderRadius: '16px 16px 0 0',
          height: '169px',
          px: '24px',
          pt: '24px',
          pb: '8px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          alignItems: 'flex-start',
          flexShrink: 0,
          boxSizing: 'border-box'
        }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '24px', width: '100%' }}>
          <CardTitle
            card={item}
            showEye={false}
            titleRef={titleRef}
            isTitleOverflowing={isTitleOverflowing}
            fallbackTitle="Report Title"
            containerSx={{ gap: '24px' }}
            titleSx={{
              color: '#000',
              fontFamily: 'Rubik',
              fontSize: '16px',
              fontWeight: 600,
              lineHeight: '150%',
              letterSpacing: '0.16px'
            }}
          />
        </Box>

        <StyledTooltip title={descriptionText} disableHoverListener={!isDescOverflowing} placement="bottom-end" tooltipSx={overflowTooltipSx} arrowSx={overflowArrowSx} popperModifiers={overflowPopperModifiers}>
          <Box
            ref={descRef}
            sx={{
              alignSelf: 'stretch',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontFamily: '"Noto Sans", sans-serif',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '150%',
              color: '#121A22',
              textAlign: 'left'
            }}>
            {descriptionText}
          </Box>
        </StyledTooltip>
      </Box>

      <Box
        sx={{
          borderRight: '1px solid #71767A',
          borderBottom: '1px solid #71767A',
          borderLeft: '1px solid #71767A',
          borderTop: 'none',
          borderRadius: '0 0 16px 16px',
          height: '76px',
          p: '24px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          flexShrink: 0,
          boxSizing: 'border-box'
        }}>
        <ReportOperation
          card={card}
          handleReportClick={handleReportClick}
          handleShareClick={handleShareClick}
          shouldShowFavorite={shouldShowFavorite}
          containerPx={0}
        />
      </Box>
    </Box>
  );
};

export default ReportCardItem;
